// src/Pages/Estabilishment/Subscription/index.tsx

import React, { useEffect, useRef, useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  MenuItem, Select, FormControl, InputLabel, CircularProgress,
} from "@mui/material";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import "dayjs/locale/pt-br";
import {
  getMySubscription,
  getMyEstabilishments,
  getPlans,
  postStripeCheckout,
  postStripeBillingPortal,
} from "../../../Services/Stripe";
import {
  IStripeSubscription,
  IRestGetMySubscription,
  IStripePlan,
  IRestGetStripePlans,
  IUserEstabilishment,
  IRestGetMyEstabilishments,
  IRestStripeCheckout,
  IRestStripeBillingPortal,
} from "../../../Services/Interfaces";
import { useT } from "../../../i18n";
import { planoPorVeiculacao } from "../../../Services/Plan";

dayjs.locale("pt-br");

// ─── Status config ────────────────────────────────────────────────────────────

const STATUS_CONFIG: Record<string, { label: string; color: string; bg: string }> = {
  active:             { label: "Ativo",              color: "#1a7a4a", bg: "#e6f7ef" },
  trialing:           { label: "Período de Teste",   color: "#0066cc", bg: "#e5f0ff" },
  past_due:           { label: "Pagamento Pendente", color: "#b45309", bg: "#fff7e6" },
  incomplete:         { label: "Incompleto",         color: "#b45309", bg: "#fff7e6" },
  incomplete_expired: { label: "Expirado",           color: "#888",    bg: "#f0f0f0" },
  canceled:           { label: "Cancelado",          color: "#b91c1c", bg: "#fef2f2" },
  unpaid:             { label: "Não Pago",           color: "#b91c1c", bg: "#fef2f2" },
};

const getStatus = (status: string) =>
  STATUS_CONFIG[status] ?? { label: status, color: "#888", bg: "#f0f0f0" };

const fmt = (date: string | null) =>
  date ? dayjs(date).format("DD/MM/YYYY") : "—";

const fmtPrice = (price: string) =>
  parseFloat(price).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

// ─── Icons ────────────────────────────────────────────────────────────────────

const ic = (p: React.ReactNode) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{p}</svg>
);

const IconCard     = () => ic(<><rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/></>);
const IconExternal = () => ic(<><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></>);
const IconCheck    = () => ic(<polyline points="20 6 9 17 4 12"/>);

// ─── Plan feature lists (matching Figma exactly) ─────────────────────────────

/** E-mail de suporte usado nos planos com condição personalizada */
const SUPPORT_EMAIL =
  process.env.REACT_APP_SUPPORT_EMAIL || "places@pertalks.com";

const PLAN_DETAILS: Record<string, {
  displayName: string;
  subtitle: string;
  popular?: boolean;
  /** Selo destacado no topo do card (ex.: "3 meses grátis") */
  flag?: string;
  /** Aviso ao pé do card, com link para o suporte */
  customNote?: boolean;
  features: string[];
}> = {
  // Insight — R$ 29,90 — 3 meses grátis, depois cobrado
  insight: {
    displayName: "Insight",
    subtitle: "Perfeito para entender seus clientes",
    flag: "3 meses grátis",
    features: [
      "Dashboard básico",
      "Total de check-ins",
      "Horários de pico",
      "Principais assuntos e interesses",
      "Identificação de tendências de comportamento dos clientes",
      "Suporte por e-mail",
    ],
  },
  // Connection — R$ 67,00 — pago desde o início (sem degustação)
  connection: {
    displayName: "Connection",
    subtitle: "Participe ativamente das conexões geradas no app",
    popular: true,
    features: [
      "Todas as vantagens do plano Insight",
      "Envio de notificações push personalizadas para usuários que fizeram check-in",
      "Destaque visual no mapa Pertalks",
      "Estatísticas avançadas de comportamento e retorno do público",
      "Pré check-in para usuários",
      "Tarefas que desbloqueiam benefícios, descontos e muito mais!",
      "Gestão de banners promocionais",
      "Suporte prioritário via WhatsApp",
      "Selo de Local Seguro",
    ],
  },
  // Showcase Pulse — R$ 199,00 — divulgação premium, raio de 10 km
  pulse: {
    displayName: "Showcase Pulse",
    subtitle: "Divulgue sua marca na home do app",
    customNote: true,
    features: [
      "Banner na vitrine da home do Pertalks",
      "Alcance de 10 km a partir do seu endereço",
      "Veiculação de 15 dias",
      "Link direto para o seu perfil",
      "Arte aprovada pela nossa equipe em até 72h",
      "Suporte prioritário",
    ],
  },
  // Showcase Wave — R$ 399,00 — maior alcance e tempo de veiculação
  wave: {
    displayName: "Showcase Wave",
    subtitle: "Alcance ainda maior para sua marca",
    customNote: true,
    features: [
      "Tudo do Showcase Pulse",
      "Alcance de 30 km a partir do seu endereço",
      "Veiculação de 30 dias",
      "Prioridade de exibição na vitrine",
      "Arte aprovada pela nossa equipe em até 72h",
      "Prioridade máxima no suporte",
    ],
  },
};

/** Resolve o tipo do plano a partir do nome salvo no banco */
const resolvePlanType = (name: string | undefined): string => {
  if (!name) return "insight";
  const lower = name.toLowerCase();
  // Pulse e Wave são planos distintos (antigo Showcase único)
  if (lower.includes("pulse"))                               return "pulse";
  if (lower.includes("wave"))                                return "wave";
  if (lower.includes("connection") || lower.includes("meet")) return "connection";
  return "insight";
};

// ─── Component ────────────────────────────────────────────────────────────────

export const Subscription = () => {
  const t = useT();

  const [subscription,    setSubscription]    = useState<IStripeSubscription | null>(null);
  const [plans,           setPlans]           = useState<IStripePlan[]>([]);
  const [myEstabs,        setMyEstabs]        = useState<IUserEstabilishment[]>([]);
  const [loading,         setLoading]         = useState(true);
  const [portalLoading,   setPortalLoading]   = useState(false);

  // ── Carrossel dos planos ──────────────────────────────────────────────────
  const plansRef = useRef<HTMLDivElement | null>(null);
  const [canScrollLeft,  setCanScrollLeft]  = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  /** Mostra a seta apenas quando existe card fora da área visível. */
  const updateScrollHints = React.useCallback(() => {
    const el = plansRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 8);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 8);
  }, []);

  /** Rola aproximadamente a largura de um card. */
  const scrollPlans = (dir: 1 | -1) => {
    const el = plansRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * 320, behavior: "smooth" });
  };

  // recalcula as setas quando os planos chegam e ao redimensionar a janela
  useEffect(() => {
    updateScrollHints();
    window.addEventListener("resize", updateScrollHints);
    return () => window.removeEventListener("resize", updateScrollHints);
  }, [plans, updateScrollHints]);
  const [activeTab,       setActiveTab]       = useState<"plans" | "payments">("plans");

  // checkout dialog
  const [checkoutOpen,    setCheckoutOpen]    = useState(false);
  const [checkoutPlan,    setCheckoutPlan]    = useState<IStripePlan | null>(null);
  const [selectedEstab,   setSelectedEstab]   = useState("");
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  useEffect(() => {
    Promise.all([getMySubscription(), getPlans(), getMyEstabilishments()])
      .then(([subRes, plansRes, estabsRes]) => {
        setSubscription((subRes   as IRestGetMySubscription).data.data);
        setPlans(
          [...((plansRes as IRestGetStripePlans).data.data ?? [])]
            .sort((a, b) => parseFloat(a.price) - parseFloat(b.price))
        );
        setMyEstabs((estabsRes    as IRestGetMyEstabilishments).data.data ?? []);
      })
      .catch(() => toast.error(t("Erro ao carregar dados da assinatura.")))
      .finally(() => setLoading(false));
  // `t` fica fora das deps de propósito: incluí-lo refaria a consulta a
  // cada troca de idioma, perdendo página, filtros e rolagem.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openCheckoutDialog = (plan: IStripePlan) => {
    setCheckoutPlan(plan);
    setSelectedEstab(myEstabs.length === 1 ? myEstabs[0].id : "");
    setCheckoutOpen(true);
  };

  const handleCheckout = () => {
    if (!checkoutPlan || !selectedEstab) { toast.warning(t("Selecione um estabelecimento.")); return; }
    setCheckoutLoading(true);
    postStripeCheckout(checkoutPlan.id, selectedEstab)
      .then((res) => { window.location.href = (res as IRestStripeCheckout).data.data.checkout_url; })
      .catch(() => toast.error(t("Erro ao iniciar checkout.")))
      .finally(() => setCheckoutLoading(false));
  };

  const handleBillingPortal = () => {
    setPortalLoading(true);
    postStripeBillingPortal()
      .then((res) => { window.location.href = (res as IRestStripeBillingPortal).data.data.portal_url; })
      .catch(() => toast.error(t("Erro ao abrir portal de pagamento.")))
      .finally(() => setPortalLoading(false));
  };

  const isCurrentPlan = (planId: string) =>
    subscription !== null &&
    subscription.status !== "canceled" &&
    subscription.plan != null &&
    subscription.plan.id === planId;

  if (loading) return (
    <div style={{ display: "flex", justifyContent: "center", paddingTop: 60 }}>
      <CircularProgress style={{ color: "#00b6bc" }} />
    </div>
  );

  const statusCfg   = subscription ? getStatus(subscription.status) : null;
  const currentType = resolvePlanType(subscription?.plan?.name);

  const tabBtn = (active: boolean): React.CSSProperties => ({
    padding: "8px 20px", border: "1.5px solid", borderRadius: 8,
    fontWeight: 600, fontSize: 13, cursor: "pointer",
    borderColor: active ? "#00b6bc" : "#e0e0e0",
    background:  active ? "#00b6bc" : "#fff",
    color:       active ? "#fff"    : "#555",
  });

  return (
    <div style={{ maxWidth: 960 }}>
      <h2 style={{ margin: "0 0 20px", fontSize: 22, fontWeight: 700, color: "#111" }}>{t("Planos")}</h2>

      {/* Tabs */}
      <div style={{ display: "flex", gap: 8, marginBottom: 28 }}>
        <button style={tabBtn(activeTab === "plans")}    onClick={() => setActiveTab("plans")}>{t("Planos")}</button>
        <button style={tabBtn(activeTab === "payments")} onClick={() => setActiveTab("payments")}>{t("Pagamentos")}</button>
      </div>

      {/* ── PLANOS tab ───────────────────────────────────────────────────────── */}
      {activeTab === "plans" && (
        <>
          {/* Subscription status banner (if active) */}
          {subscription && !["canceled","incomplete_expired"].includes(subscription.status) && (
            <div style={{ background: "#fff", border: "1.5px solid #e8e8e8", borderRadius: 14, padding: "18px 22px", marginBottom: 28, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
                  <span style={{ fontSize: 16, fontWeight: 700, color: "#111" }}>
                    {subscription.plan?.name ?? PLAN_DETAILS[currentType]?.displayName ?? t("Plano")}
                  </span>
                  <span style={{ padding: "3px 10px", borderRadius: 999, fontSize: 12, fontWeight: 600, color: statusCfg!.color, background: statusCfg!.bg }}>
                    {t(statusCfg!.label)}
                  </span>
                </div>
                <div style={{ fontSize: 13, color: "#888" }}>
                  {t("Período: {inicio} → {fim}", {
                    inicio: fmt(subscription.currentPeriodStart),
                    fim: fmt(subscription.currentPeriodEnd),
                  })}
                </div>
              </div>
              <button
                onClick={handleBillingPortal}
                disabled={portalLoading}
                style={{ display: "flex", alignItems: "center", gap: 6, padding: "9px 18px", background: "#fff", border: "1.5px solid #00b6bc", borderRadius: 10, color: "#00b6bc", fontWeight: 600, fontSize: 13, cursor: "pointer" }}
              >
                {portalLoading ? <CircularProgress size={13} color="inherit" /> : <IconCard />}
                {t("Gerenciar assinatura")}
                <IconExternal />
              </button>
            </div>
          )}

          {/* Plan cards */}
          <div style={{ marginBottom: 12 }}>
            <h3 style={{ margin: "0 0 4px", fontSize: 18, fontWeight: 700, color: "#111" }}>{t("Conheça os nossos planos")}</h3>
            <p style={{ margin: "0 0 24px", fontSize: 13, color: "#888" }}>{t("Assine um dos planos Pertalks e obtenha vantagens e benefícios para o seu estabelecimento.")}</p>
          </div>

          {/* Faixa rolável — mantém os 4 planos na MESMA linha.
              A seta à direita sinaliza que há mais planos fora da tela. */}
          <div style={{ position: "relative" }}>

            {canScrollRight && (
              <button
                type="button"
                aria-label={t("Ver mais planos")}
                onClick={() => scrollPlans(1)}
                style={{
                  position: "absolute", right: -6, top: "50%",
                  transform: "translateY(-50%)", zIndex: 3,
                  width: 40, height: 40, borderRadius: "50%",
                  border: "1.5px solid #e8e8e8", background: "#fff",
                  color: "#00b6bc", fontSize: 20, lineHeight: 1,
                  cursor: "pointer", boxShadow: "0 2px 10px rgba(0,0,0,.12)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}
              >
                ›
              </button>
            )}

            {canScrollLeft && (
              <button
                type="button"
                aria-label={t("Ver planos anteriores")}
                onClick={() => scrollPlans(-1)}
                style={{
                  position: "absolute", left: -6, top: "50%",
                  transform: "translateY(-50%)", zIndex: 3,
                  width: 40, height: 40, borderRadius: "50%",
                  border: "1.5px solid #e8e8e8", background: "#fff",
                  color: "#00b6bc", fontSize: 20, lineHeight: 1,
                  cursor: "pointer", boxShadow: "0 2px 10px rgba(0,0,0,.12)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}
              >
                ‹
              </button>
            )}

          <div
            ref={plansRef}
            onScroll={updateScrollHints}
            style={{
              display: "flex",
              gap: 20,
              overflowX: "auto",
              scrollBehavior: "smooth",
              paddingBottom: 8,
              scrollbarWidth: "thin",
            }}
          >
            {plans.length === 0 ? (
              <p style={{ color: "#aaa", fontSize: 14 }}>{t("Nenhum plano disponível no momento.")}</p>
            ) : plans.map((plan) => {
              const current    = isCurrentPlan(plan.id);
              const planType   = resolvePlanType(plan.name);
              const details    = PLAN_DETAILS[planType] ?? { displayName: plan.name, subtitle: "", features: [] };
              // Nome exibido vem do backend (Pulse / Wave / etc.), pois Pulse e Wave
              // compartilham o mesmo tipo interno "showcase" mas têm nomes distintos.
              const displayName = plan.name ?? details.displayName;

              return (
                <div key={plan.id} style={{
                  flex: "0 0 auto",
                  width: 300,
                  background: "#fff",
                  border: `1.5px solid ${current ? "#00b6bc" : "#e8e8e8"}`,
                  borderRadius: 16,
                  padding: "28px 24px",
                  display: "flex",
                  flexDirection: "column",
                  boxShadow: current ? "0 0 0 3px rgba(0,182,188,0.15)" : "none",
                  position: "relative",
                }}>
                  {/* "Mais popular" badge */}
                  {details.popular && (
                    <div style={{ position: "absolute", top: 18, right: 18, background: "#e6f9fa", color: "#00b6bc", fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 999 }}>
                      {t("Mais popular")}
                    </div>
                  )}

                  {/* Flag do plano (ex.: 3 meses grátis) */}
                  {details.flag && !details.popular && (
                    <div style={{
                      position: "absolute", top: 18, right: 18,
                      background: "#cdf3f5", color: "#0b6f73",
                      fontSize: 11, fontWeight: 700,
                      padding: "3px 10px", borderRadius: 999,
                    }}>
                      {t(details.flag)}
                    </div>
                  )}

                  {/* Plan name + subtitle */}
                  <h3 style={{ margin: "0 0 6px", fontSize: 22, fontWeight: 700, color: "#111" }}>{displayName}</h3>
                  <p style={{ margin: "0 0 16px", fontSize: 13, color: "#888", lineHeight: 1.4 }}>{t(details.subtitle)}</p>

                  {/* Price */}
                  <div style={{ marginBottom: 20 }}>
                    <span style={{ fontSize: 28, fontWeight: 700, color: "#00b6bc" }}>
                      {fmtPrice(plan.price)}
                    </span>
                    {/* Pulse e Wave são por tempo de veiculação (15 e 30
                        dias), não por mês — o preço deles vai sem sufixo. */}
                    {!planoPorVeiculacao(plan.name) && (
                      <span style={{ fontSize: 13, color: "#aaa" }}>{t("/mês")}</span>
                    )}
                  </div>

                  {/* Features */}
                  <div style={{ flex: 1, marginBottom: 24 }}>
                    {details.features.map((f, i) => (
                      <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 10 }}>
                        <span style={{ color: "#00b6bc", flexShrink: 0, marginTop: 1 }}><IconCheck /></span>
                        <span style={{ fontSize: 13, color: "#333", lineHeight: 1.4 }}>{t(f)}</span>
                      </div>
                    ))}
                  </div>

                  {/* Pulse/Wave: raios e períodos fora do padrão são negociados
                      direto com a Pertalks. Fica ACIMA do botão para que os
                      botões "Assinar" fiquem alinhados entre todos os cards. */}
                  {details.customNote && (
                    <p style={{
                      margin: "0 0 14px", fontSize: 11, color: "#999",
                      lineHeight: 1.6, textAlign: "center",
                    }}>
                      {t("Para raios e períodos personalizados,")}{" "}
                      <a
                        href={`mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(
                          t("Plano personalizado — {plano}", { plano: displayName }),
                        )}&body=${encodeURIComponent(
                          t(
                            "Olá! Gostaria de saber mais sobre raios e períodos personalizados no plano {plano}.",
                            { plano: displayName },
                          ),
                        )}`}
                        style={{ color: "#00b6bc", fontWeight: 600, textDecoration: "underline" }}
                      >
                        {t("entre em contato com a Pertalks")}
                      </a>.
                    </p>
                  )}

                  {/* CTA button */}
                  {current ? (
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, width: "100%", padding: "12px", background: "#f0f0f0", color: "#aaa", borderRadius: 10, fontWeight: 600, fontSize: 14 }}>
                      <IconCheck /> {t("Plano atual")}
                    </div>
                  ) : (
                    <button
                      onClick={() => openCheckoutDialog(plan)}
                      style={{ width: "100%", padding: "12px", background: "#00b6bc", color: "#fff", border: "none", borderRadius: 10, fontWeight: 700, fontSize: 14, cursor: "pointer" }}
                    >
                      {t("Assinar")}
                    </button>
                  )}
                </div>
              );
            })}
          </div>

            {canScrollRight && (
              <p style={{ margin: "8px 0 0", fontSize: 11, color: "#bbb" }}>
                {t("Arraste para o lado ou use a seta para ver todos os planos")}
              </p>
            )}
          </div>
        </>
      )}

      {/* ── PAGAMENTOS tab ────────────────────────────────────────────────────── */}
      {activeTab === "payments" && (
        <div style={{ background: "#fff", border: "1.5px solid #e8e8e8", borderRadius: 14, padding: "28px 24px" }}>
          <h3 style={{ margin: "0 0 16px", fontSize: 16, fontWeight: 700, color: "#111" }}>{t("Histórico de pagamentos")}</h3>
          {subscription && !["canceled","incomplete_expired"].includes(subscription.status) ? (
            <div>
              <p style={{ fontSize: 13, color: "#888", margin: "0 0 16px" }}>
                {t("Acesse o portal de pagamentos para visualizar todas as suas faturas e histórico completo.")}
              </p>
              <button
                onClick={handleBillingPortal}
                disabled={portalLoading}
                style={{ display: "flex", alignItems: "center", gap: 6, padding: "10px 20px", background: "#00b6bc", border: "none", borderRadius: 10, color: "#fff", fontWeight: 600, fontSize: 13, cursor: "pointer" }}
              >
                {portalLoading ? <CircularProgress size={13} color="inherit" /> : <IconCard />}
                {t("Ver faturas & pagamentos")}
                <IconExternal />
              </button>
            </div>
          ) : (
            <p style={{ fontSize: 14, color: "#aaa" }}>{t("Nenhuma assinatura ativa para exibir pagamentos.")}</p>
          )}
        </div>
      )}

      {/* ── Checkout Dialog ───────────────────────────────────────────────────── */}
      <Dialog open={checkoutOpen} onClose={() => setCheckoutOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle style={{ fontWeight: 700 }}>
          {t("Assinar {plano}", {
            plano: checkoutPlan
              ? (PLAN_DETAILS[resolvePlanType(checkoutPlan.name)]?.displayName ?? checkoutPlan.name)
              : "",
          })}
        </DialogTitle>
        <DialogContent>
          {myEstabs.length > 1 && (
            <FormControl fullWidth style={{ marginTop: 8 }}>
              <InputLabel>{t("Estabelecimento")}</InputLabel>
              <Select value={selectedEstab} label={t("Estabelecimento")} onChange={(e) => setSelectedEstab(e.target.value)}>
                {myEstabs.map((e) => <MenuItem key={e.id} value={e.id}>{e.estabilishment?.name ?? e.id}</MenuItem>)}
              </Select>
            </FormControl>
          )}
          {myEstabs.length === 1 && (
            <p style={{ fontSize: 14, color: "#555", margin: "8px 0 0" }}>
              {t("Estabelecimento")}: <strong>{myEstabs[0].estabilishment?.name ?? ""}</strong>
            </p>
          )}
        </DialogContent>
        <DialogActions style={{ padding: "16px 24px", gap: 8 }}>
          <button onClick={() => setCheckoutOpen(false)} style={{ padding: "10px 24px", borderRadius: 10, border: "1.5px solid #ddd", background: "#fff", cursor: "pointer", fontWeight: 600, fontSize: 14, color: "#555" }}>
            {t("Cancelar")}
          </button>
          <button
            onClick={handleCheckout}
            disabled={checkoutLoading || (!selectedEstab)}
            style={{ padding: "10px 24px", borderRadius: 10, border: "none", background: "#00b6bc", cursor: "pointer", fontWeight: 600, fontSize: 14, color: "#fff", opacity: (!selectedEstab || checkoutLoading) ? 0.6 : 1 }}
          >
            {checkoutLoading ? <CircularProgress size={14} color="inherit" /> : t("Ir para pagamento")}
          </button>
        </DialogActions>
      </Dialog>
    </div>
  );
};